import { useState } from 'react';
import { GoArrowLeft, GoArrowRight } from 'react-icons/go';
import { useLanguage } from '@/i18n/LanguageContext';
import { ScrollReveal } from '@podus/ui';

const TOTAL = 5;

export default function Testimonials() {
    const { t } = useLanguage();
    const [current, setCurrent] = useState(0);
    const [fading, setFading] = useState(false);

    const navigate = (dir: number) => {
        if (fading) return;
        setFading(true);
        setTimeout(() => {
            setCurrent((prev) => (prev + dir + TOTAL) % TOTAL);
            setFading(false);
        }, 250);
    };

    return (
        <section className="px-4 py-8 lg:px-16 lg:py-20 xl:px-20">
            <ScrollReveal variant="fade-up" distance={36} duration={800}>
                <div className="rounded-3xl bg-[#F4F4F4] px-6 py-12 lg:px-16 lg:py-16 xl:px-20 xl:py-20">
                    {/* Title */}
                    <ScrollReveal variant="blur-up" delay={100} duration={800}>
                        <h2 className="text-secondary text-[28px] font-extrabold leading-[1.1] tracking-tight sm:text-[36px] lg:text-center lg:text-[56px] xl:text-[64px]">
                            {t('testimonials.heading')}
                        </h2>
                    </ScrollReveal>

                    {/* Quote */}
                    <div className="mt-8 lg:mt-12">
                        <blockquote
                            className="text-secondary text-base font-bold leading-[1.5] tracking-tight sm:text-lg lg:text-[28px] xl:text-[32px]"
                            style={{ opacity: fading ? 0 : 1, transition: 'opacity 0.25s ease' }}
                        >
                            <span className="text-secondary/40">{'\u201C'} </span>
                            {t(`testimonials.${current}.quote`)}
                            <span className="text-secondary/40">{'\u201D'}</span>
                        </blockquote>
                    </div>

                    {/* Bottom: Author + Navigation */}
                    <div className="mt-10 flex flex-col gap-8 lg:mt-14 lg:flex-row lg:items-end lg:justify-between">
                        {/* Author Info */}
                        <div style={{ opacity: fading ? 0 : 1, transition: 'opacity 0.25s ease' }}>
                            <p className="text-secondary text-sm font-semibold sm:text-base">
                                {t(`testimonials.${current}.name`)}
                            </p>
                            <p className="text-secondary-100 mt-0.5 text-xs sm:text-sm">
                                {t(`testimonials.${current}.role`)}
                            </p>
                        </div>

                        {/* Navigation */}
                        <div className="flex items-center gap-4">
                            <button
                                type="button"
                                onClick={() => navigate(-1)}
                                className="border-secondary/20 hover:border-secondary/50 cursor-pointer rounded-full border px-6 py-3 transition-colors"
                                aria-label="Previous testimonial"
                            >
                                <GoArrowLeft className="text-secondary h-5 w-5" />
                            </button>

                            <span className="min-w-12 select-none text-center text-sm">
                                <span className="text-secondary font-bold">{String(current + 1).padStart(2, '0')}</span>
                                <span className="text-secondary-100">/{String(TOTAL).padStart(2, '0')}</span>
                            </span>

                            <button
                                type="button"
                                onClick={() => navigate(1)}
                                className="bg-secondary hover:bg-secondary-400 cursor-pointer rounded-full px-6 py-3 transition-colors"
                                aria-label="Next testimonial"
                            >
                                <GoArrowRight className="h-5 w-5 text-white" />
                            </button>
                        </div>
                    </div>
                </div>
            </ScrollReveal>
        </section>
    );
}
